/**
 * Unsaved Changes Hook.
 *
 * Protects users from losing form data when navigating away from a
 * page with unsaved changes. Provides:
 *
 * - Browser `beforeunload` warning for tab close/refresh
 * - Manual navigation blocking via state (pages call `checkUnsaved`
 *   before navigating and render the dialog when blocked)
 */

import { useEffect, useState, useCallback } from "react";

interface UseUnsavedChangesResult {
  /** Whether the unsaved changes dialog should be shown. */
  isBlocked: boolean;
  /** Call to proceed with the pending navigation. */
  proceed: () => void;
  /** Call to cancel the pending navigation (stay on page). */
  reset: () => void;
  /** Call before navigating — returns true if navigation should proceed, false if blocked. */
  checkUnsaved: (onProceed: () => void) => boolean;
}

export function useUnsavedChanges(isDirty: boolean): UseUnsavedChangesResult {
  const [isBlocked, setIsBlocked] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  // Browser beforeunload warning for tab close/refresh.
  useEffect(() => {
    if (!isDirty) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const checkUnsaved = useCallback(
    (onProceed: () => void): boolean => {
      if (!isDirty) {
        onProceed();
        return true;
      }
      setPendingAction(() => onProceed);
      setIsBlocked(true);
      return false;
    },
    [isDirty],
  );

  const proceed = useCallback(() => {
    setIsBlocked(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }, [pendingAction]);

  const reset = useCallback(() => {
    setIsBlocked(false);
    setPendingAction(null);
  }, []);

  return { isBlocked, proceed, reset, checkUnsaved };
}
